import { useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { ITableColumn } from "../Table/TableColumn";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { TableWrapper, TableLoader, TableHeaderCell, Align } from "../Table/style";
import { IFundingData } from "../../hooks/api/funding/useFundingData";
import { COLORS } from "../../constants/design/colors";
import { formatAmount } from "../../utils/strings";
import { getValueColor } from "./utils";

interface IFundingTableProps {
  fundingData: IFundingData[];
  isLoading: boolean;
}

export function FundingTable({ fundingData, isLoading }: IFundingTableProps) {
  const columns: ITableColumn<IFundingData>[] = useMemo(
    () => [
      {
        title: "Pair",
        accessor: "Pair",
        align: "left",
        Cell: ({ value }: { value: string }) => (
          <div
            style={{
              backgroundColor: COLORS.blue.two,
              color: COLORS.blue.one,
              padding: "4px 12px",
              borderRadius: "12px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {value}
          </div>
        ),
      },
      {
        title: "Aevo Funding",
        accessor: "Aevo_Funding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Binance Funding",
        accessor: "Binance_Funding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Bybit Funding",
        accessor: "Bybit_Funding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const fundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(fundingValue),
              }}
            >
              {fundingValue > 0 ? "+" : ""}
              {formatAmount(fundingValue)}
            </Align>
          );
        },
      },
      {
        title: "Net Funding",
        accessor: "Net_Funding",
        align: "right",
        Cell: ({ value }: { value: number }) => {
          const netFundingValue = Number(value);
          return (
            <Align
              align="right"
              style={{
                color: getValueColor(netFundingValue),
                fontWeight: "bold",
              }}
            >
              {netFundingValue > 0 ? "+" : ""}
              {formatAmount(netFundingValue)}
            </Align>
          );
        },
      },
      ...(fundingData.length > 0 && fundingData[0].Date
        ? [
          {
            title: "Date",
            accessor: "Date",
            align: "left",
            Cell: ({ value }: { value: string }) => <div>{value}</div>,
          } as ITableColumn<IFundingData>,
        ]
        : []),
    ],
    [fundingData]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: fundingData,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
        initialState: {
          sortBy: [
            {
              id: fundingData.length > 0 && fundingData[0].Date ? "Date" : "Pair",
              desc: !!(fundingData.length > 0 && fundingData[0].Date),
            },
          ],
        },
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
          {!isLoading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: "center" }}>
                No funding data available for the selected period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </TableWrapper>
  );
}
